/**
 * fuzzyMatch.js
 *
 * Implements fuzzy string matching for Hard Mode typed answers.
 * Uses Levenshtein distance with a small threshold to forgive
 * minor typos while keeping matching strict enough to be fair.
 *
 * Match pipeline:
 * 1. Normalize input via alias map (normalizeCollege)
 * 2. Exact case-insensitive match
 * 3. Levenshtein distance ≤ threshold (scales with string length)
 */

import { normalizeCollege, canonicalize } from './normalizeCollege';

/**
 * Computes the Levenshtein edit distance between two strings.
 * Uses the Wagner-Fischer dynamic programming algorithm — O(n*m) time, O(min(n,m)) space.
 *
 * @param {string} a
 * @param {string} b
 * @returns {number} - edit distance
 */
export function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  // Use shorter string as the "column" dimension for space efficiency
  if (a.length > b.length) [a, b] = [b, a];

  const aLen = a.length;
  const bLen = b.length;
  let prev = Array.from({ length: aLen + 1 }, (_, i) => i);
  let curr = new Array(aLen + 1);

  for (let j = 1; j <= bLen; j++) {
    curr[0] = j;
    for (let i = 1; i <= aLen; i++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[i] = Math.min(
        prev[i] + 1,       // deletion
        curr[i - 1] + 1,   // insertion
        prev[i - 1] + cost  // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }

  return prev[aLen];
}

/**
 * Determines the acceptable Levenshtein threshold based on string length.
 * Shorter strings get a tighter threshold to prevent false positives.
 *
 * @param {string} str - the canonical answer
 * @returns {number} - max allowed edit distance
 */
function getThreshold(str) {
  const len = str.length;
  if (len <= 4) return 1;
  if (len <= 8) return 2;
  return 3;
}

/**
 * Checks if a user's typed answer matches the correct college.
 *
 * @param {string} userInput - what the user typed
 * @param {string} correctCollege - the canonical CSV college name
 * @returns {boolean} - true if match is accepted
 */
export function isMatch(userInput, correctCollege) {
  // Step 1: Normalize user input through alias map
  const normalized = normalizeCollege(userInput);
  const canonical = correctCollege.trim();

  // Step 2: Exact case-insensitive match after normalization
  if (canonicalize(normalized) === canonicalize(canonical)) {
    return true;
  }

  // Step 3: Fuzzy match — Levenshtein distance within threshold
  const distance = levenshtein(
    canonicalize(normalized),
    canonicalize(canonical)
  );

  return distance <= getThreshold(canonical);
}
